import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, EmptyState, Section, Kpi, Pill, Table, Th, Td, fmt, DistributionBar, Btn } from '../components/ui-kit';
import { Activity, RefreshCw, Send, ShieldCheck, Target } from 'lucide-react';

interface InvestorBoardData {
  status: string;
  summary: {
    total_contacts: number;
    email_present: number;
    dns_enriched: number;
    dns_root_present: number;
    fit_computed: number;
    band_s: number;
    band_a: number;
    band_b: number;
    fit_99: number;
    email_sent: number;
    email_opened: number;
    link_clicked: number;
    nda_signed: number;
    safe_intent: number;
  };
  dns: { domains_total: number; dns_valid: number; mx_present?: number; dmarc_present?: number };
  ip_probe: { attempted: number; eligible: number; ok: number };
  email_log: { total: number; queued: number; sent: number; blocked: number; test_send: number; live: number; dry_run: number };
  investor_room: { registered: number; nda_signed: number; docs_viewed: number; ansarada_ready: number };
  by_band: Array<{ band: string; n: number; avg_score: number }>;
  crm_stages: Array<{ stage: string; n: number }>;
  top_prospects: Array<{
    subject_id: string;
    name?: string;
    firstname?: string;
    lastname?: string;
    fund_name?: string;
    email?: string;
    country?: string;
    investor_type?: string;
    fit_score?: number;
    fit_band?: string;
    completeness_pct?: number;
    enrichment_level?: number;
    crm_state?: string;
  }>;
  gate: { complete: boolean; checks: Record<string, boolean> };
}

const toneFor = (value?: boolean) => value ? 'live' : 'alert';

export default function InvestorBoard() {
  const { getToken } = useXyqarAuth();
  const [data, setData] = React.useState<InvestorBoardData | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    try {
      const token = await getToken().catch(() => '');
      const board = await apiGet<InvestorBoardData>('/api/v1/admin/investors/board', token);
      setData(board);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message ?? 'Investor board unavailable');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  if (loading) {
    return <Card><p className="text-sm text-white/50"><RefreshCw className="h-4 w-4 inline animate-spin mr-2" />Loading investor board...</p></Card>;
  }

  if (!data) {
    return <EmptyState title="Investor board unavailable" hint={err ?? undefined} />;
  }

  const bandData: Array<{ k: string; v: number; tone: 'mute' | 'live' | 'warn' | 'alert' }> =
    (data.by_band ?? []).map((b) => ({
      k: b.band,
      v: b.n,
      tone: b.band === 'S' || b.band === 'A' ? 'live' : b.band === 'B' ? 'warn' : 'mute',
    }));
  const checks = data.gate?.checks ?? {};

  return (
    <div>
      <PageTitle
        kicker="Prospection and investor follow-up"
        title="Investor board"
        right={
          <div className="flex items-center gap-2">
            <Pill tone={data.gate.complete ? 'live' : 'warn'}>
              <ShieldCheck className="h-3 w-3" /> gate {data.gate.complete ? 'pass' : 'open'}
            </Pill>
            <Btn onClick={refresh}><RefreshCw className="h-3 w-3" /> Refresh</Btn>
          </div>
        }
      />

      {err && <Card className="mb-6 border-amber-500/30 bg-amber-500/5"><p className="text-amber-300 text-xs font-mono">{err}</p></Card>}

      <Section title="Corpus control" hint="125k target, DNS, scoring and dispatch state from the Investor Brain tables.">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi accent="blue" label="Contacts" value={fmt(data.summary.total_contacts)} hint={`${fmt(data.summary.email_present)} with email`} />
          <Kpi accent="green" label="DNS enriched" value={fmt(data.summary.dns_enriched)} hint={`${fmt(data.summary.dns_root_present)} roots`} />
          <Kpi accent="amber" label="Fit computed" value={fmt(data.summary.fit_computed)} hint={`${fmt(data.summary.fit_99)} at 99%+`} />
          <Kpi accent="green" label="Queued mail" value={fmt(data.email_log.queued)} hint={`${fmt(data.email_log.sent)} sent, ${fmt(data.email_log.blocked)} blocked`} />
        </div>
      </Section>

      <Section title="Gate checks">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {Object.entries(checks).map(([key, ok]) => (
            <Card key={key}>
              <Pill tone={toneFor(ok)}>{ok ? 'pass' : 'fail'}</Pill>
              <p className="text-xs text-white/70 mt-3 font-mono">{key.replace(/_/g, ' ')}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Priority distribution" hint="CRM priority is ordered by fit score, then completeness.">
        <Card>
          <DistributionBar data={bandData} />
        </Card>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Kpi label="DNS valid domains" value={fmt(data.dns.dns_valid)} hint={`${fmt(data.dns.domains_total)} domains`} />
          <Kpi label="IP probes OK" value={fmt(data.ip_probe.ok)} hint={`${fmt(data.ip_probe.attempted)} attempted`} />
          <Kpi label="Room NDAs" value={fmt(data.investor_room.nda_signed)} hint={`${fmt(data.investor_room.docs_viewed)} doc events`} />
          <Kpi label="Ansarada ready" value={fmt(data.investor_room.ansarada_ready)} hint="NDA level or above" />
        </div>
      </Section>

      <Section title="CRM state machine" right={<Pill tone="live"><Activity className="h-3 w-3" /> auto-refresh 30s</Pill>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(data.crm_stages ?? []).slice(0, 12).map((stage) => (
            <Card key={stage.stage}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{stage.stage}</p>
              <p className="text-2xl text-white mt-2 font-mono">{fmt(stage.n)}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Top prospects" hint="Lot 1 candidates come from the top of this ordered ledger.">
        {data.top_prospects.length === 0 && <EmptyState title="No scored investors visible" />}
        {data.top_prospects.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Investor</Th><Th>Fund</Th><Th>Fit</Th><Th>State</Th><Th>Email</Th>
              </tr>
            </thead>
            <tbody>
              {data.top_prospects.map((p) => (
                <tr key={p.subject_id} className="border-t border-white/5 hover:bg-white/5">
                  <Td>
                    <span className="text-white text-sm">{p.name || `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || 'Unknown'}</span>
                    <p className="text-[10px] text-white/30 font-mono">{p.country || p.investor_type || p.subject_id.slice(0, 12)}</p>
                  </Td>
                  <Td><span className="text-white/70 text-xs">{p.fund_name || '-'}</span></Td>
                  <Td><Pill tone={p.fit_band === 'S' || p.fit_band === 'A' ? 'live' : 'warn'}><Target className="h-3 w-3" />{p.fit_band || 'NA'} {((p.fit_score ?? 0) * 100).toFixed(1)}%</Pill></Td>
                  <Td><Pill tone={p.crm_state === 'email_sent' ? 'live' : p.crm_state === 'email_queued' ? 'warn' : 'mute'}>{p.crm_state || 'raw'}</Pill></Td>
                  <Td><span className="text-[11px] text-white/45 font-mono"><Send className="h-3 w-3 inline mr-1" />{p.email || '-'}</span></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
